import { CartNoteUpdateEvent } from '@shopify/events';

/* Native Shopify components own all cart requests, including debounced automatic note saves. */
class PetifoCartTools extends HTMLElement {
  #events;

  connectedCallback() {
    this.#events?.abort();
    this.#events = new AbortController();
    this.addEventListener('click', this.#toggle, { signal: this.#events.signal });
    this.addEventListener('input', this.#noteInput, { signal: this.#events.signal });
    this.addEventListener(CartNoteUpdateEvent.eventName, this.#noteUpdate, { signal: this.#events.signal });
    this.addEventListener('keydown', this.#escape, { signal: this.#events.signal });
  }

  disconnectedCallback() {
    this.#events?.abort();
  }

  #toggle = (event) => {
    const button = event.target.closest?.('[data-pf-tool]');
    if (!button || button.closest('pf-cart-tools') !== this) return;
    const panel = this.querySelector(`#${button.getAttribute('aria-controls')}`);
    if (!panel) return;
    const open = button.getAttribute('aria-expanded') !== 'true';
    if (open) {
      for (const other of this.querySelectorAll('[data-pf-tool]')) {
        if (other === button) continue;
        other.setAttribute('aria-expanded', 'false');
        const otherPanel = this.querySelector(`#${other.getAttribute('aria-controls')}`);
        if (otherPanel) otherPanel.hidden = true;
      }
    }
    button.setAttribute('aria-expanded', String(open));
    panel.hidden = !open;
    if (open) {
      // Keep focus in the activating gesture so mobile keyboards can open.
      // The fixed footer must not scroll the product list into the field.
      panel.querySelector('input[name="discount"], textarea[name="note"]')?.focus({ preventScroll: true });
    }
  };

  #noteInput = (event) => {
    const field = event.target;
    if (!(field instanceof HTMLTextAreaElement) || field.name !== 'note') return;
    // Both page and drawer can submit the same cart form. Mirror the latest text
    // immediately, including an empty note, before a customer presses checkout.
    for (const other of document.querySelectorAll('cart-note textarea[name="note"]')) {
      if (other !== field) other.value = field.value;
    }
    const error = field.closest('cart-note')?.querySelector('[role="alert"]');
    if (error) error.hidden = true;
  };

  #noteUpdate = (update) => {
    const nativeNote = update.target;
    if (!(nativeNote instanceof Element) || nativeNote.closest('pf-cart-tools') !== this) return;
    const field = nativeNote.querySelector('textarea[name="note"]');
    const error = nativeNote.querySelector('[role="alert"]');
    update.promise?.catch((failure) => {
      if (failure?.name === 'AbortError' || !this.isConnected || field?.value !== update.note) return;
      if (error) error.hidden = false;
    });
  };

  #escape = (event) => {
    if (event.key !== 'Escape') return;
    const panel = event.target.closest?.('.pf-cart-tools__panel');
    if (!panel || panel.hidden) return;
    const button = this.querySelector(`[aria-controls="${panel.id}"]`);
    if (!button) return;
    event.preventDefault();
    event.stopPropagation();
    panel.hidden = true;
    button.setAttribute('aria-expanded', 'false');
    button.focus({ preventScroll: true });
  };
}

if (!customElements.get('pf-cart-tools')) {
  customElements.define('pf-cart-tools', PetifoCartTools);
}
